import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { ValidationError } from "@cervval.js/common/src/forms/validationError";
import { RegisterValidator, Validator } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { DEFAULT_DATE_FORMAT, UnixTimestamp, UnixTimestampIndeterminateInterval, format, formatFinishDate, isBetween, isInside, subtract } from "@cervval.js/common/src/tools/timestamps.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { DateIntervalPickerSelection } from "../../../pickers/dateIntervalPicker/dateIntervalPicker";
import { i18n } from "./dateEditorValidatorI18n/i18n";


export type DateOrDateInterval = UnixTimestamp | DateIntervalPickerSelection;

export type DateInRangesValidatorParams = { ranges: UnixTimestampIndeterminateInterval[] };
/**
 * This validator ensures a date is contained in one of the given ranges
 * sample use in propertyConfig => validators: validatorEntry(DateInRangesValidator, {ranges: [{start: parse(...).uts, finish: parse(...).uts}]})
 * or validators: { dateInRanges: {ranges: [{start: parse(...).uts, finish: parse(...).uts}]} } (second option requires this class to be loaded)
 */
@RegisterValidator("dateInRanges")
export class DateInRangesValidator implements Validator<DateOrDateInterval, DateInRangesValidatorParams> {

    /**
     * @returns true if :
     * - propertyValue is not set
     * - no ranges are provided
     * - value is set and satisfy one of the ranges constraints
     */
    public isValid(property: PropertyConfig, propertyValue: DateOrDateInterval, validatorParams: DateInRangesValidatorParams, allProperties: Record<string, PropertyConfig>, allPropertiesValue: Record<string, unknown>): boolean {
        if (propertyValue == null) { return true; }

        const ranges = validatorParams.ranges;
        // If no range given or empty, all range is enable
        if (!ranges.length) { return true; }

        return this.checkRange(propertyValue, ranges);
    }

    /**
     * @returns error message to show when not valid
     */
    public getError(lang: string, property: PropertyConfig, propertyValue: UnixTimestamp, validatorParams: DateInRangesValidatorParams, allProperties: Record<string, PropertyConfig>, allPropertiesValue: Record<string, anyReallyAny>): ValidationError {

        const i18nValidator = i18n(lang);

        // Check type of propertyValue
        const propertyValueIsInterval = isUnixTimestampInterval(propertyValue);

        let errorMessageRendered = i18nValidator.ranges({
            value: this.formatPropertyValue(propertyValue, lang)
        });

        validatorParams.ranges.forEach((range, i) => {// TODO rework, @see MR 1104
            errorMessageRendered += `${i > 0 ? "," : ""} ${this.renderRange(range, propertyValueIsInterval, lang)}`;
        });

        return { errorMessage: errorMessageRendered };
    }

    /**
     * Render UnixTimestampInterval for display
     * @param range UnixTimestampIndeterminateInterval
     * @param isInterval True if the range is for an interval value
     * @returns 
     */
    public renderRange(range: UnixTimestampIndeterminateInterval, isInterval: boolean, lang: string): string {
        const i18nValidator = i18n(lang);
        const formatFunc = isInterval ? formatFinishDate : format;

        const start = range.start;
        const finish = range.finish;

        if (start == null || finish == null) {
            if (start != null) {// Start defined
                return `${i18nValidator.from} ${format(start, DEFAULT_DATE_FORMAT)}`; // TODO handle date format for "en" i18n
            } else if (finish != null) {// Finish defined
                return `${i18nValidator.until} ${formatFunc(finish, DEFAULT_DATE_FORMAT)}`; // TODO handle date format for "en" i18n
            }
            return "";// Start & Finish not defined
        }

        return `${format(start, DEFAULT_DATE_FORMAT)}-${formatFunc(finish, DEFAULT_DATE_FORMAT)}`; // TODO handle date format for "en" i18n
    }

    /**
     * Return true if propertyValue is included in one range 
     * @param propertyValue UnixTimestamp or date interval to test
     * @param ranges list of all valid ranges
     */
    public checkRange(propertyValue: DateOrDateInterval, ranges: UnixTimestampIndeterminateInterval[]): boolean {

        // Check type of propertyValue
        const propertyValueIsInterval = isUnixTimestampInterval(propertyValue);

        for (const range of ranges) {
            if (propertyValueIsInterval) {
                if (!propertyValue.start || !propertyValue.finish) { return false; }

                const isIntervalStartInRange = propertyValue.start ? isInside(propertyValue.start, range.start, range.finish) : true;
                const isIntervalFinishInRange = propertyValue.finish ? isInside(subtract(propertyValue.finish, { day: 1 }).uts, range.start, range.finish) : true;

                if (isIntervalStartInRange && isIntervalFinishInRange) {
                    return true;
                }
            } else {
                // Increment by one day to have end boundary included
                if (isBetween(propertyValue, range.start, range.finish, true)) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Format propertyValue to display error message
     * @param propertyValue UnixTimestamp or date interval to format
     * @param lang locale
     * @returns formatted propertyValue of default message
     */
    public formatPropertyValue(propertyValue: DateOrDateInterval, lang: string): string {
        let displayString = undefined;
        // Check type of propertyValue
        if (isUnixTimestampInterval(propertyValue)) {
            if (propertyValue.start && propertyValue.finish) {
                displayString = `[${format(propertyValue.start, DEFAULT_DATE_FORMAT)}-${format(propertyValue.finish, DEFAULT_DATE_FORMAT)}]`; // TODO handle date format for "en" i18n
            }
        } else {
            displayString = format(propertyValue, DEFAULT_DATE_FORMAT); // TODO handle date format for "en" i18n
        }

        return displayString ?? i18n(lang).noValue;
    }
}

/**
 * Allow us to define if value is DateIntervalPickerSelection type
 * @param value to test
 */
export function isUnixTimestampInterval(value: DateOrDateInterval): value is DateIntervalPickerSelection {
    return typeof value !== "number";
}